// 📁 src/lightversion/config/learninglite.js

export const LEARNING_EXPLORATIONS = [
  {
    id: "innovator_mindset",
    title: "Test Your Innovator Mindset",
    subtitle: "How you think before you build",
    duration: "8–10 mins",
    description:
      "Explore how you approach uncertainty, conviction, and trade-offs as an innovator.",

    prompts: (context) => [
      `When faced with an unclear problem like "${context.purpose}", what is your first instinct — explore broadly or narrow quickly? Why?`,
      `Describe a moment where you chose learning over speed. What triggered that decision?`,
      `How do you usually react when early assumptions are challenged?`,
      `What feels more uncomfortable to you: moving without clarity or waiting too long?`,
      `If this direction (${context.direction || "your chosen direction"}) fails, what would you want to learn from it?`,
    ],

    synthesizeInsight: (answers) =>
      `Your responses suggest that you tend to ${
        answers.length > 3
          ? "reflect before committing"
          : "act quickly under uncertainty"
      }. Strong innovators balance conviction with adaptability — this awareness becomes powerful as you validate ideas.`,

    // ✅ ADDITIONS
    proTeaser:
      "In Pro, your mindset is mapped against founder archetypes so you know where to compensate with structure.",
    recommendAfter: "problem_solving_style",
  },

  {
    id: "problem_solving_style",
    title: "Test Your Problem-Solving Style",
    subtitle: "How you work through ambiguity",
    duration: "8–10 mins",
    description:
      "Understand how you deconstruct problems and move toward solutions.",

    prompts: (context) => [
      `When a problem feels overwhelming, what do you usually do first?`,
      `How do you decide which part of a problem matters most?`,
      `Do you prefer structured frameworks or intuition-led exploration? Why?`,
      `What kind of problems energize you the most?`,
      `In this problem space, what feels hardest to reason about?`,
    ],

    synthesizeInsight: (answers) =>
      `You appear to approach problems with a ${
        answers.some((a) => a.length > 100)
          ? "reflective"
          : "pragmatic"
      } style. Knowing this helps you design validation steps that match your natural strengths.`,

    // ✅ ADDITIONS
    proTeaser:
      "Pro introduces structured problem-framing tools used by experienced founders to reduce blind spots.",
    recommendAfter: "creativity_lens",
  },

  {
    id: "creativity_lens",
    title: "Test Your Creativity Lens",
    subtitle: "How you generate new possibilities",
    duration: "7–9 mins",
    description:
      "Discover how you form new ideas and connect insights.",

    prompts: () => [
      `Where do your best ideas usually come from?`,
      `Do you remix existing solutions or seek entirely new angles?`,
      `What inspires you outside your main domain?`,
      `When was the last time you surprised yourself with an idea?`,
      `How do constraints affect your creativity?`,
    ],

    synthesizeInsight: () =>
      `Your creativity seems to emerge through pattern recognition and reflection. Many strong founders build originality by recombining familiar elements in new ways.`,

    // ✅ ADDITIONS
    proTeaser:
      "In Pro, creativity is converted into repeatable ideation sprints instead of one-off inspiration.",
    recommendAfter: "domain_awareness",
  },

  {
    id: "domain_awareness",
    title: "Test Your Domain Awareness",
    subtitle: "How grounded your intuition is",
    duration: "6–8 mins",
    description:
      "Reflect on how closely you observe real-world signals in your chosen space.",

    prompts: (context) => [
      `Who experiences this problem most frequently?`,
      `What workarounds do people currently use?`,
      `Why hasn’t this problem been solved well yet?`,
      `What signals tell you this problem is real?`,
      `What would make you doubt your current understanding?`,
    ],

    synthesizeInsight: () =>
      `Your reflections indicate growing awareness of real-world constraints. This grounding becomes critical when transitioning from ideas to validation.`,

    // ✅ ADDITIONS
    proTeaser:
      "Pro deepens domain insight using market lenses, user segmentation, and validation playbooks.",
    recommendAfter: null, // final exploration
  },
];

export const getLearningContext = (liteJourney) => ({
  purpose: liteJourney?.purpose?.statement || "your current purpose",
  direction: liteJourney?.direction?.title || null,
});
